import React, { useState, useEffect, useRef } from 'react';
import { 
  NoteWithDetails, 
  getNotePhotoUrl, 
  updateNote, 
  deleteNotePhoto,
  uploadNotePhoto 
} from '../api/client';

interface Props {
  note: NoteWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  token: string;
}

export function EditNoteModal({ note, isOpen, onClose, onSave, token }: Props) {
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (note) {
      setContent(note.content);
      setCurrentPhotoUrl(note.photo_url);
      setError(null);
    }
  }, [note]);

  if (!isOpen || !note) return null;

  const photoUrl = getNotePhotoUrl(currentPhotoUrl);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Note content is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateNote(token, note.id, content.trim());
      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setIsDeletingPhoto(true);
    setError(null);

    try {
      await deleteNotePhoto(token, note.id);
      setCurrentPhotoUrl(null);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete photo');
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.heic')) {
      setError('Please select a valid image file (JPG, PNG, WebP, or HEIC)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Photo must be less than 10MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const result = await uploadNotePhoto(note.id, file);
      setCurrentPhotoUrl(result.photo_url);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const roomsList = note.rooms
    .map(r => `${r.room_number}${r.room_name ? ` (${r.room_name})` : ''}`)
    .join(', ');

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Edit Note</h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0 4px'
            }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {error && (
            <div style={{
              background: '#fee',
              color: '#c00',
              padding: '10px 12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Metadata */}
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#666'
          }}>
            <div><strong>Author:</strong> {note.author_name}</div>
            <div><strong>Created:</strong> {formatDate(note.created_at)}</div>
            <div><strong>Rooms:</strong> {roomsList}</div>
          </div>

          {/* Photo section */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              Photo
            </label>
            
            {photoUrl ? (
              <div>
                <img
                  src={photoUrl}
                  alt="Note attachment"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginBottom: '8px'
                  }}
                  onClick={() => window.open(photoUrl, '_blank')}
                />
                <div>
                  <button
                    onClick={handleDeletePhoto}
                    disabled={isDeletingPhoto}
                    style={{
                      padding: '8px 16px',
                      background: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isDeletingPhoto ? 'wait' : 'pointer',
                      fontSize: '13px',
                      opacity: isDeletingPhoto ? 0.6 : 1
                    }}
                  >
                    {isDeletingPhoto ? 'Deleting...' : 'Delete Photo'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,.heic"
                  onChange={handleUploadPhoto}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  style={{
                    padding: '10px 16px',
                    background: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: isUploadingPhoto ? 'wait' : 'pointer',
                    fontSize: '14px',
                    opacity: isUploadingPhoto ? 0.6 : 1
                  }}
                >
                  {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#666' }}>
                  No photo attached. JPG, PNG, WebP, or HEIC. Max 10MB.
                </p>
              </div>
            )}
          </div>

          {/* Content editor */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
              Note Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              padding: '10px 20px',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !content.trim()}
            style={{
              padding: '10px 20px',
              background: isSaving || !content.trim() ? '#ccc' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isSaving || !content.trim() ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
